import React, { useState, useEffect } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { FiX } from 'react-icons/fi';
import httpService from 'services/httpService.js';
import { store } from 'Store';
import displayGlobalMessage from 'Utils/DisplayGlobalMessage';
import CustomCKEditor from 'Components/CustomCKEditor';
import { createAnnouncementAsync } from '../services/announcementService';
import styles from '../AnnouncementsV2.module.css';

const CreateAnnouncementModal = ({
  show,
  onHide,
  onSuccess,
  editAnnouncement,
  courseId
}) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = !!editAnnouncement;

  // Reset form when modal opens/closes or edit announcement changes
  useEffect(() => {
    if (show) {
      if (editAnnouncement) {
        setTitle(editAnnouncement.title || '');
        setContent(editAnnouncement.description || '');
      } else {
        setTitle('');
        setContent('');
      }
    }
  }, [show, editAnnouncement]);

  // Get user from store
  const getUser = () => {
    const state = store.getState();
    return state.userInfo;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!title.trim()) {
      displayGlobalMessage('Please enter an announcement title', 'error');
      return;
    }

    if (!content.trim()) {
      displayGlobalMessage('Please enter announcement content', 'error');
      return;
    }

    setIsSubmitting(true);

    const user = getUser();
    const formData = new FormData();
    formData.append('title', title.trim());
    formData.append('description', content);

    try {
      if (isEditMode) {
        // Update existing announcement (use old endpoint)
        const url = `/announcements/api/announcement/${editAnnouncement.id}/edit_announcement/?format=json`;
        await httpService.instance({
          method: 'patch',
          url: url,
          data: formData,
          headers: {
            Authorization: `Token ${user.token}`,
            'Content-Type': 'multipart/form-data',
          },
        });
        displayGlobalMessage('Announcement updated successfully', 'success');
        onSuccess();
      } else {
        // Create new announcement using async V2 endpoint
        // Email notification is controlled by course settings
        const response = await createAnnouncementAsync(courseId, {
          title: title.trim(),
          description: content
        });

        if (response.email_task_id) {
          // Email sending started
          displayGlobalMessage(
            `Announcement created! Sending emails to ${response.total_recipients} students...`,
            'success'
          );
          // Pass the task info to parent for progress tracking
          onSuccess({ emailTaskId: response.email_task_id, totalRecipients: response.total_recipients });
        } else if (response.email_warning) {
          // Announcement created but email not available
          displayGlobalMessage(`Announcement created. ${response.email_warning}`, 'warning');
          onSuccess();
        } else {
          // Announcement created without email
          displayGlobalMessage('Announcement created successfully', 'success');
          onSuccess();
        }
      }
    } catch (error) {
      console.error('Error saving announcement:', error);
      displayGlobalMessage(
        isEditMode ? 'Failed to update announcement' : 'Failed to create announcement',
        'error'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContentChange = (data) => {
    setContent(data.html || data.text || '');
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      backdrop="static"
      keyboard={false}
      enforceFocus={false}
    >
      <Modal.Header className={styles.modalHeader}>
        <Modal.Title className={styles.modalTitle}>
          {isEditMode ? 'Edit Announcement' : 'Create New Announcement'}
        </Modal.Title>
        <button className={styles.modalCloseButton} onClick={onHide}>
          <FiX size={20} />
        </button>
      </Modal.Header>

      <Modal.Body className={styles.modalBody}>
        <form onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Announcement Title</label>
            <input
              type="text"
              className={styles.formInput}
              placeholder="Enter announcement title..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Content</label>
            <div className={styles.editorContainer}>
              <CustomCKEditor
                name={`AnnouncementV2-${isEditMode ? editAnnouncement?.id : 'new'}`}
                placeholder="Write your announcement..."
                value={content}
                onChange={handleContentChange}
                config={{ resize_enabled: false }}
              />
            </div>
          </div>
        </form>
      </Modal.Body>

      <Modal.Footer className={styles.modalFooter}>
        <button
          type="button"
          className={styles.cancelButton}
          onClick={onHide}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className={styles.submitButton}
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting && <Spinner animation="border" size="sm" />}
          {isEditMode ? 'Update Announcement' : 'Publish Announcement'}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateAnnouncementModal;
